import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import priceService from '../../../services/priceService';

const RateDisplay = ({ exchangeRate, lastUpdate }) => {
  const [countdown, setCountdown] = useState(30);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch live rates
  const fetchLiveRates = async () => {
    setLoading(true);
    try {
      const ratePairs = [
        { from: 'BTC', to: 'ETH' },
        { from: 'ETH', to: 'USDC' },
        { from: 'BTC', to: 'USDC' },
        { from: 'USDC', to: 'ETH' }
      ];

      const ratePromises = ratePairs?.map(async (pair) => {
        const rate = await priceService?.getExchangeRate(pair?.from, pair?.to);
        const fromData = await priceService?.getPriceWithChange(pair?.from);
        return {
          from: pair?.from,
          to: pair?.to,
          rate: rate || 0,
          change: fromData?.change24h || 0
        };
      });

      const fetchedRates = await Promise.all(ratePromises);
      setRates(fetchedRates);
    } catch (error) {
      console.error('Error fetching live rates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveRates();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          fetchLiveRates();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="card h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-1">Live Rates</h3>
          <p className="text-sm text-muted-foreground">Real-time exchange rates</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-xs font-bold text-primary">{countdown}</span>
          </div>
          <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
        </div>
      </div>

      {loading && rates?.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-muted-foreground">Loading rates...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {rates?.map((rate, index) => {
            const isPositive = rate?.change >= 0;
            return (
              <div 
                key={index} 
                className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-smooth cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{rate?.from}</span>
                    <Icon name="ArrowRight" size={14} className="text-muted-foreground" />
                    <span className="text-sm font-semibold text-foreground">{rate?.to}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon 
                      name={isPositive ? 'TrendingUp' : 'TrendingDown'} 
                      size={12} 
                      className={isPositive ? 'text-success' : 'text-error'}
                    />
                    <span className={`text-xs font-medium ${isPositive ? 'text-success' : 'text-error'}`}>
                      {isPositive ? '+' : ''}{rate?.change?.toFixed(2)}%
                    </span>
                  </div>
                </div>
                <p className="text-lg font-bold text-foreground">
                  {rate?.rate?.toLocaleString('en-US', { maximumFractionDigits: 6 })}
                </p>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Info" size={14} />
          <span>Rates update every 30 seconds</span>
        </div>
      </div>
    </div>
  );
};

export default RateDisplay;